import { useTracking } from '@folklore/tracking';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import { useEffect, useRef, useState, ReactNode } from 'react';

import { NOTIFICATION_NAMESPACE } from '../../contexts/PopupsContext';
import CloseButton from '../buttons/CloseButton';
import PopupPortal from './PopupPortal';

import styles from '../../styles/popups/notification.module.css';

export interface NotificationProps {
    id: string;
    trackingId?: string | null;
    opened?: boolean;
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    withTransition?: boolean;
    withScrollLock?: boolean;
    withoutCloseButton?: boolean;
    className?: string | null;
    style?: React.CSSProperties | null;
    children?: ReactNode;
    requestClose?: (() => void) | null;
    onOpened?: (() => void) | null;
    onClosed?: (() => void) | null;
}

const Notification = ({
    id,
    trackingId = null,
    opened = true,
    position = 'bottom-right',
    withTransition = false,
    withScrollLock = false,
    withoutCloseButton = false,
    className = null,
    style = null,
    children = null,
    requestClose = null,
    onOpened = null,
    onClosed = null,
}: NotificationProps) => {
    const tracking = useTracking();
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [transitionAxis, setTransitionAxis] = useState('y');
    const containerRef = useRef();
    const spring = useSpring({
        from: {
            [transitionAxis]: '100%',
            // scale: opened ? 0.8 : 1,
            opacity: 0,
        },
        to: {
            [transitionAxis]: opened ? '0%' : '100%',
            // scale: opened ? 1 : 0.8,
            opacity: opened ? 1 : 0,
        },
        // [transitionAxis]: opened ? '0%' : '100%',
        // opacity: opened ? 1 : 0,
        onStart: () => {
            setIsTransitioning(true);
        },
        onResolve: () => {
            if (!isTransitioning) {
                return;
            }
            if (opened && withScrollLock) {
                disableBodyScroll(containerRef.current);
            } else if (!opened && withScrollLock) {
                clearAllBodyScrollLocks();
            }
            if (opened && onOpened !== null) {
                onOpened();
            } else if (!opened && onClosed !== null) {
                onClosed();
            }
            setIsTransitioning(false);
        },
    });

    useEffect(() => {
        if (withTransition) {
            return;
        }
        if (opened && withScrollLock) {
            disableBodyScroll(containerRef.current);
        } else if (!opened && withScrollLock) {
            clearAllBodyScrollLocks();
        }
        if (opened && onOpened !== null) {
            onOpened();
        } else if (!opened && onClosed !== null) {
            onClosed();
        }
    }, [opened, onOpened, onClosed, withTransition]);

    useEffect(
        () => () => {
            if (withScrollLock) {
                clearAllBodyScrollLocks();
            }
        },
        [withScrollLock],
    );

    const bindSwipe = useDrag(
        ({ swipe: [swipeX, swipeY] }) => {
            if (swipeX !== 0 || swipeY !== 0) {
                setTransitionAxis(Math.abs(swipeX) > Math.abs(swipeY) ? 'x' : 'y');
            }

            if (swipeX === 1 || swipeY === 1) {
                requestClose();
            }
        },
        {
            enabled: opened,
            preventScroll: true,
            // axis: 'xy',
        },
    );

    const onClickClose = () => {
        requestClose();
    };

    useEffect(() => {
        tracking.trackNotification(trackingId || id, opened ? 'open' : 'close');
    }, [trackingId, id, opened]);

    return (
        <PopupPortal id={id} requestClose={requestClose} namespace={NOTIFICATION_NAMESPACE}>
            <div
                style={style}
                className={classNames([
                    styles.container,
                    {
                        [styles.isTransitioning]: withTransition && isTransitioning,
                    },
                    styles[camelCase(position)],
                    className,
                ])}
                data-nosnippet
                {...bindSwipe()}
                ref={containerRef}
            >
                <animated.div style={withTransition ? spring : null} className={styles.transition}>
                    {children}
                    {!withoutCloseButton ? (
                        <CloseButton onClick={onClickClose} className={styles.closeButton} />
                    ) : null}
                </animated.div>
            </div>
        </PopupPortal>
    );
};

export default Notification;
